// WallCard
import React from "react";
import { fmt2, num, wallCode } from "../../../helpers/rooms.helpers";
import OpeningsPane from "./OpeningsPane";
import ElectricPane from "./ElectricPane";

export default function WallCard({
    room,
    ri,
    wall,
    wi,
    wallComputed,
    open,
    onToggle,
    onUpdateWall,
    onRemoveWall,
    onAddDoor,
    onAddWindow,
    onUpdateOpening,
    onRemoveOpening,
    onAddPoint,
    onUpdatePoint,
    onRemovePoint,
    allRooms,
    onAddDoorGlobal,
    onUpdateOpeningGlobal,
    onRemoveOpeningGlobal,
    onUpdateOpeningsBatch,
    onRemoveOpeningsBatch,
}) {
    const canRemoveWall = room.shape === "polygon";
    const code = wall.code || wallCode(ri, wi);

    return (
        <div className={["smeta-rooms__wall", open ? "smeta-rooms__wall--open" : "smeta-rooms__wall--collapsed"].join(" ")}>
            <div className="smeta-rooms__wall-top">
                <button type="button" className="smeta-rooms__wall-toggle" onClick={onToggle}>
                    <div className="smeta-rooms__wall-code">{code}</div>
                    <div className="smeta-rooms__wall-toggle-meta">
                        <div className="smeta-rooms__wall-toggle-title">
                            {wall.name ? wall.name : `Стена ${code}`}
                        </div>
                        <div className="smeta-rooms__wall-toggle-sub">
                            <span>Нетто: <b>{fmt2(wallComputed?.netM2)}</b> м²</span>
                            <span>Проёмы: <b>{fmt2(wallComputed?.openingsM2)}</b> м²</span>
                            <span>Длина: <b>{fmt2((Number(wall.lengthMm || 0) || 0) / 1000)}</b> м</span>
                            <span>Двери: <b>{wallComputed?.doorsQty || 0}</b></span>
                            <span>Окна: <b>{wallComputed?.windowsQty || 0}</b></span>
                        </div>
                    </div>
                    <span className={`smeta-rooms__chev ${open ? "smeta-rooms__chev--open" : ""}`} aria-hidden="true" />
                </button>

                <button
                    type="button"
                    className="smeta-rooms__btn smeta-rooms__btn--danger smeta-rooms__btn--compact"
                    disabled={!canRemoveWall}
                    onClick={onRemoveWall}
                    title={!canRemoveWall ? "В прямоугольной комнате 4 стены фиксированы" : "Удалить стену"}
                >
                    Удалить
                </button>
            </div>

            {open ? (
                <>
                    <div className="smeta-rooms__wall-fields">
                        <div className="smeta-rooms__field">
                            <div className="smeta-rooms__label">Название</div>
                            <input
                                className="smeta-rooms__input"
                                value={wall.name || ""}
                                onChange={(e) => onUpdateWall({ name: e.target.value })}
                                placeholder="Напр. 'стена к окну'"
                            />
                        </div>

                        <div className="smeta-rooms__field">
                            <div className="smeta-rooms__label">Длина, мм</div>
                            <input
                                className="smeta-rooms__input"
                                value={wall.lengthMm ?? ""}
                                onChange={(e) => onUpdateWall({ lengthMm: num(e.target.value) })}
                                inputMode="numeric"
                            />
                        </div>

                        <div className="smeta-rooms__field">
                            <div className="smeta-rooms__label">Высота (override), мм</div>
                            <input
                                className="smeta-rooms__input"
                                value={wall.heightMmOverride ?? ""}
                                onChange={(e) => onUpdateWall({ heightMmOverride: num(e.target.value) })}
                                inputMode="numeric"
                            />
                        </div>

                        <div className="smeta-rooms__wall-stats">
                            <div>
                                Брутто: <b>{fmt2(wallComputed?.grossM2)}</b> м²
                            </div>
                            <div>
                                Проёмы: <b>{fmt2(wallComputed?.openingsM2)}</b> м²
                            </div>
                            <div>
                                Нетто: <b>{fmt2(wallComputed?.netM2)}</b> м²
                            </div>
                        </div>
                    </div>

                    <div className="smeta-rooms__grid-2">
                        <OpeningsPane
                            kind="doors"
                            title="Двери"
                            items={wall.doors}
                            onAdd={onAddDoor}
                            onAddDoorGlobal={onAddDoorGlobal}
                            onChange={(oi, patch) => onUpdateOpening("doors", oi, patch)}
                            onRemove={(oi) => onRemoveOpening("doors", oi)}
                            allRooms={allRooms}
                            selfRi={ri}
                            selfWi={wi}
                            onUpdateOpeningGlobal={onUpdateOpeningGlobal}
                            onRemoveOpeningGlobal={onRemoveOpeningGlobal}
                            onUpdateOpeningsBatch={onUpdateOpeningsBatch}
                            onRemoveOpeningsBatch={onRemoveOpeningsBatch}
                        />

                        <OpeningsPane
                            kind="windows"
                            title="Окна"
                            items={wall.windows}
                            onAdd={onAddWindow}
                            onChange={(oi, patch) => onUpdateOpening("windows", oi, patch)}
                            onRemove={(oi) => onRemoveOpening("windows", oi)}
                        />
                    </div>

                    <div className="smeta-rooms__electric">
                        <div className="smeta-rooms__electric-head">
                            <div className="smeta-rooms__sub-title-sm">Электрика (на этой стене)</div>
                        </div>

                        <div className="smeta-rooms__grid-2">
                            <ElectricPane
                                title="Розетки"
                                items={wall.sockets}
                                defaultSection="2.5"
                                sections={["1.5", "2.5", "4", "6"]}
                                onAdd={() => onAddPoint("sockets")}
                                onChange={(pi, patch) => onUpdatePoint("sockets", pi, patch)}
                                onRemove={(pi) => onRemovePoint("sockets", pi)}
                            />

                            <ElectricPane
                                title="Выключатели"
                                items={wall.switches}
                                defaultSection="1.5"
                                sections={["1.5", "2.5", "4"]}
                                onAdd={() => onAddPoint("switches")}
                                onChange={(pi, patch) => onUpdatePoint("switches", pi, patch)}
                                onRemove={(pi) => onRemovePoint("switches", pi)}
                            />
                        </div>
                    </div>

                    <div className="smeta-rooms__field-wide">
                        <div className="smeta-rooms__label">Заметки по стене</div>
                        <textarea
                            className="smeta-rooms__textarea"
                            value={wall.notes || ""}
                            onChange={(e) => onUpdateWall({ notes: e.target.value })}
                        />
                    </div>
                </>
            ) : null}
        </div>
    );
}
